import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton } from 'components/modals';
import { SyutSelect, type SelectOption } from 'components/ui';
import { EnterpriseScreenShell, actionButtonFactory, useCan } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { reportService } from 'api/services';
import type {
  TrialBalanceResultDto, TrialBalanceLedgerRowDto, TrialBalanceQueryParams, LedgerStatementDto,
} from 'api/types';
import { useAuthContext } from 'contexts/AuthContext';
import ReportHeader from 'components/reports/ReportHeader';
import { VoucherViewModal } from 'components/reports/VoucherViewModal';

// ============================================================================
// TRIAL BALANCE — one entity for a period, in base currency, as a tree:
// account type → subtype → group → ledger (customer / supplier ledgers rolled
// up per group until expanded). Drill: ledger row → its statement for the same
// period and business units → voucher (read-only, every line).
// Business units follow the user's rights: a single-BU user is held to theirs;
// others may add or remove BUs (none picked = all they can access).
// Income and expense open at the financial-year start; earlier years show as
// one "Profit & Loss b/f" line under Equity.
// ============================================================================

const today = () => new Date().toISOString().slice(0, 10);
const money = (n?: number | null) => (n ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');
const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '');
const fmtBal = (n: number) => (n ? `${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${n > 0 ? 'Dr' : 'Cr'}` : '0.00');
const dr = (v: number) => (v > 0 ? v : 0);
const cr = (v: number) => (v < 0 ? -v : 0);
const inp: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.375rem 0.625rem' };
const muted: React.CSSProperties = { color: 'var(--text-muted)' };
const th: React.CSSProperties = { padding: '0.45rem 0.6rem', fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '0.3rem 0.6rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const esc = (s?: string | null) => (s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

type NodeKind = 'type' | 'subtype' | 'group' | 'party' | 'ledger' | 'plbf';
interface TbNode {
  key: string;
  label: string;
  kind: NodeKind;
  depth: number;
  ledger?: TrialBalanceLedgerRowDto;
  opening: number;
  debit: number;
  credit: number;
  closing: number;
  children: TbNode[];
}
type ViewLevel = 'GROUPS' | 'LEDGERS';

const node = (key: string, label: string, kind: NodeKind, depth: number): TbNode =>
  ({ key, label, kind, depth, opening: 0, debit: 0, credit: 0, closing: 0, children: [] });

/** Builds the type → subtype → group → ledger tree and rolls the figures up. */
function buildTree(data: TrialBalanceResultDto): TbNode[] {
  const groups = new Map(data.groups.map(g => [g.id, g]));
  const types = new Map<string, { node: TbNode; order: number }>();
  const subtypeOrder = new Map<string, number>();

  const typeNode = (code: string | null | undefined, name: string | null | undefined, order: number) => {
    const k = `t:${code ?? 'NONE'}`;
    if (!types.has(k)) types.set(k, { node: node(k, name ?? 'Unclassified', 'type', 0), order });
    return types.get(k)!.node;
  };
  const child = (parent: TbNode, key: string, label: string, kind: NodeKind) => {
    let c = parent.children.find(x => x.key === key);
    if (!c) { c = node(key, label, kind, parent.depth + 1); parent.children.push(c); }
    return c;
  };
  const groupChain = (id: string): string[] => {
    const chain: string[] = [];
    let cur: string | null | undefined = id;
    while (cur && groups.has(cur) && !chain.includes(cur)) { chain.unshift(cur); cur = groups.get(cur)!.parentGroupId; }
    return chain;
  };

  for (const l of data.ledgers) {
    const t = typeNode(l.accountTypeCode, l.accountTypeName, l.typeOrder);
    const subKey = `${t.key}|s:${l.subtypeId ?? 'none'}`;
    subtypeOrder.set(subKey, l.subtypeOrder);
    let parent = child(t, subKey, l.subtypeName ?? 'Other', 'subtype');
    if (l.groupId) {
      for (const gid of groupChain(l.groupId)) parent = child(parent, `${parent.key}|g:${gid}`, groups.get(gid)!.groupName, 'group');
    }
    if (l.ledgerKind === 'PARTY') parent = child(parent, `${parent.key}|party`, 'Customer / supplier ledgers', 'party');
    const leaf = child(parent, `l:${l.ledgerId}`, `${l.ledgerName}`, 'ledger');
    leaf.ledger = l;
  }

  if (data.profitAndLossBroughtForward) {
    const eq = typeNode('EQUITY', 'Equity', [...types.values()].find(x => x.node.key === 't:EQUITY')?.order ?? 3);
    const pl = child(eq, 'plbf', 'Profit & Loss b/f (years before the financial-year start)', 'plbf');
    pl.opening = pl.closing = data.profitAndLossBroughtForward;
  }

  const roll = (n: TbNode): void => {
    if (n.ledger) { n.opening = n.ledger.opening; n.debit = n.ledger.debit; n.credit = n.ledger.credit; n.closing = n.ledger.closing; return; }
    if (n.kind === 'plbf') return;
    n.children.forEach(roll);
    n.opening = n.children.reduce((s, c) => s + c.opening, 0);
    n.debit = n.children.reduce((s, c) => s + c.debit, 0);
    n.credit = n.children.reduce((s, c) => s + c.credit, 0);
    n.closing = n.children.reduce((s, c) => s + c.closing, 0);
    const rank = (c: TbNode) => (c.kind === 'ledger' ? 2 : c.kind === 'party' ? 3 : c.kind === 'plbf' ? 4 : 1);
    n.children.sort((a, b) => rank(a) - rank(b)
      || (a.kind === 'subtype' ? (subtypeOrder.get(a.key) ?? 99) - (subtypeOrder.get(b.key) ?? 99) : 0)
      || (a.kind === 'group' ? (groups.get(a.key.split('|g:').pop()!)?.displayOrder ?? 0) - (groups.get(b.key.split('|g:').pop()!)?.displayOrder ?? 0) : 0)
      || (a.ledger && b.ledger ? a.ledger.ledgerCode.localeCompare(b.ledger.ledgerCode) : a.label.localeCompare(b.label)));
    if (n.kind === 'party') n.label = `Customer / supplier ledgers (${n.children.length})`;
  };
  const roots = [...types.values()].sort((a, b) => a.order - b.order).map(x => x.node);
  roots.forEach(roll);
  return roots;
}

/** Expanded keys for a view level: GROUPS stops above the ledgers; LEDGERS opens all but the party roll-ups. */
function expandedFor(roots: TbNode[], level: ViewLevel): Set<string> {
  const keys = new Set<string>();
  const walk = (n: TbNode) => {
    const open = level === 'LEDGERS' ? n.kind !== 'party' : (n.kind === 'type' || n.kind === 'subtype');
    if (open && n.children.length) keys.add(n.key);
    n.children.forEach(walk);
  };
  roots.forEach(walk);
  return keys;
}

interface TrialBalanceScreenProps {
  moduleIcon?: string;
  tabId?: string;
}

const TrialBalanceScreen: React.FC<TrialBalanceScreenProps> = ({ moduleIcon, tabId }) => {
  const can = useCan('trial-balance');
  const { selectedEntity, selectedFinancialYear, accessibleBusinessUnits, accessibleFinancialYears } = useAuthContext();

  const fy = useMemo(
    () => accessibleFinancialYears.find(f => f.id === selectedFinancialYear?.id),
    [accessibleFinancialYears, selectedFinancialYear],
  );
  const defaultFrom = fy?.startDate?.slice(0, 10) ?? today();
  const defaultTo = fy?.endDate && fy.endDate.slice(0, 10) < today() ? fy.endDate.slice(0, 10) : today();

  const [filterOpen, setFilterOpen] = useState(false);
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);
  const [buIds, setBuIds] = useState<string[]>([]);
  const [includeZero, setIncludeZero] = useState(false);
  const [applied, setApplied] = useState<TrialBalanceQueryParams>({ fromDate: defaultFrom, toDate: defaultTo });

  const [data, setData] = useState<TrialBalanceResultDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [level, setLevel] = useState<ViewLevel>('LEDGERS');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Drill-down
  const [ledgerRow, setLedgerRow] = useState<TrialBalanceLedgerRowDto | null>(null);
  const [statement, setStatement] = useState<LedgerStatementDto | null>(null);
  const [voucherId, setVoucherId] = useState<string | null>(null);

  // Business units the user may pick in this entity. One → fixed to it.
  const buOptions: SelectOption[] = useMemo(
    () => accessibleBusinessUnits
      .filter(b => !selectedEntity || b.entityId === selectedEntity.id)
      .map(b => ({ value: b.id, label: b.buName })),
    [accessibleBusinessUnits, selectedEntity],
  );
  const singleBu = buOptions.length === 1;

  const load = useCallback(async (params: TrialBalanceQueryParams) => {
    setLoading(true);
    try {
      const result = await reportService.getTrialBalance(params);
      setData(result);
      setExpanded(expandedFor(buildTree(result), level));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load the trial balance.');
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(applied); }, [applied, load]);

  const apply = () => {
    if (!fromDate || !toDate) { notificationService.toast.warning('Choose the period.'); return; }
    if (fromDate > toDate) { notificationService.toast.warning('The from date is after the to date.'); return; }
    setLedgerRow(null); setStatement(null);
    setApplied({ fromDate, toDate, businessUnitIds: buIds.length ? buIds : undefined, includeZeroBalance: includeZero || undefined });
    setFilterOpen(false);
  };

  const tree = useMemo(() => (data ? buildTree(data) : []), [data]);
  const changeLevel = (l: ViewLevel) => { setLevel(l); setExpanded(expandedFor(tree, l)); };
  const toggle = (key: string) => setExpanded(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  const visible = useMemo(() => {
    const out: TbNode[] = [];
    const walk = (n: TbNode) => { out.push(n); if (expanded.has(n.key)) n.children.forEach(walk); };
    tree.forEach(walk);
    return out;
  }, [tree, expanded]);

  const openLedger = useCallback(async (row: TrialBalanceLedgerRowDto) => {
    setLedgerRow(row);
    setStatement(null);
    try {
      setStatement(await reportService.getTrialBalanceLedger(row.ledgerId, applied));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load the ledger.');
      setLedgerRow(null);
    }
  }, [applied]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try { await reportService.exportTrialBalance(applied); }
    catch (err: any) { notificationService.toast.error(err?.response?.data?.message || 'Could not export the trial balance.'); }
    finally { setExporting(false); }
  }, [applied]);

  // Print what is on screen (the expanded tree), without the app chrome.
  const handlePrint = useCallback(() => {
    if (!data) return;
    const rows = visible.map(n => {
      const strong = n.kind === 'type' || n.kind === 'subtype';
      const label = `${'&nbsp;'.repeat(n.depth * 4)}${esc(n.ledger ? `${n.ledger.ledgerCode} · ${n.label}` : n.label)}`;
      return `<tr${strong ? ' class="b"' : ''}><td>${label}</td><td class="n">${money(dr(n.opening))}</td><td class="n">${money(cr(n.opening))}</td><td class="n">${money(n.debit)}</td><td class="n">${money(n.credit)}</td><td class="n">${money(dr(n.closing))}</td><td class="n">${money(cr(n.closing))}</td></tr>`;
    }).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Trial Balance</title><style>
      body{font-family:Segoe UI,Arial,sans-serif;font-size:10.5px;color:#111;margin:16px}
      h1{font-size:16px;margin:0 0 4px} .meta{color:#555;margin-bottom:8px}
      table{width:100%;border-collapse:collapse} th{background:#eef2f7;text-align:right;padding:4px 6px} th:first-child{text-align:left}
      td{padding:2px 6px;border-bottom:1px solid #eee} .n{text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums}
      .b td{font-weight:700} .t td{font-weight:700;border-top:2px solid #999} .w{margin-top:8px;color:#92400e} @page{size:A4 landscape;margin:12mm}
    </style></head><body>
      <h1>Trial Balance</h1>
      <div class="meta">${esc(data.entityName)} · ${esc(fmtDate(data.fromDate))} to ${esc(fmtDate(data.toDate))} · ${esc(data.baseCurrencyCode)} · ${esc(data.businessUnits.length ? data.businessUnits.map(b => b.name).join(', ') : 'All business units')}</div>
      <table><tr><th>Particulars</th><th>Opening Dr</th><th>Opening Cr</th><th>Debit</th><th>Credit</th><th>Closing Dr</th><th>Closing Cr</th></tr>
      ${rows}
      <tr class="t"><td>Total</td><td class="n">${money(data.openingDebit)}</td><td class="n">${money(data.openingCredit)}</td><td class="n">${money(data.periodDebit)}</td><td class="n">${money(data.periodCredit)}</td><td class="n">${money(data.closingDebit)}</td><td class="n">${money(data.closingCredit)}</td></tr>
      </table>
      ${data.warnings.map(w => `<div class="w">Note: ${esc(w)}</div>`).join('')}
      <script>window.onload=function(){window.print();}</script></body></html>`;
    const w = window.open('', '_blank');
    if (!w) { notificationService.toast.warning('Allow pop-ups to print.'); return; }
    w.document.write(html);
    w.document.close();
  }, [data, visible]);

  const inLedger = !!ledgerRow;
  const actionButtons: ActionButton[] = inLedger
    ? [
        actionButtonFactory.refresh(() => ledgerRow && openLedger(ledgerRow)),
        actionButtonFactory.custom('back', <IconSystem.arrowLeft size={18} />, 'Back to Trial Balance',
          () => { setLedgerRow(null); setStatement(null); }, { tooltip: 'Back to the trial balance' }),
      ]
    : [
        actionButtonFactory.refresh(() => load(applied)),
        ...(can('EXPORT') ? [{ ...actionButtonFactory.export_(handleExport), disabled: !data || exporting }] : []),
        { ...actionButtonFactory.print(handlePrint, 'Print or save as PDF'), disabled: !data },
      ];

  const buLabel = data
    ? (data.businessUnits.length ? data.businessUnits.map(b => b.name).join(', ') : 'All business units')
    : '—';
  const balanced = data ? Math.abs(data.difference) < 0.005 : true;

  const amountCells = (n: { opening: number; debit: number; credit: number; closing: number }, bold = false) => {
    const s: React.CSSProperties = bold ? { ...td, fontWeight: 700 } : td;
    return (
      <>
        <td style={s}>{money(dr(n.opening))}</td>
        <td style={s}>{money(cr(n.opening))}</td>
        <td style={s}>{money(n.debit)}</td>
        <td style={s}>{money(n.credit)}</td>
        <td style={s}>{money(dr(n.closing))}</td>
        <td style={s}>{money(cr(n.closing))}</td>
      </>
    );
  };

  return (
    <EnterpriseScreenShell
      title={inLedger ? `Trial Balance › ${ledgerRow!.ledgerCode} · ${ledgerRow!.ledgerName}` : 'Trial Balance'}
      description={inLedger
        ? `${fmtDate(applied.fromDate)} – ${fmtDate(applied.toDate)} · ${buLabel} · click a voucher to see all its lines`
        : 'Opening, movements and closing of every ledger — click a ledger to see its entries, then a voucher'}
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.chart size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={inLedger ? undefined : { onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
      trailingContent={
        !inLedger && filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close"><IconSystem.x size={14} /></button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From</label>
                <input type="date" className="form-input" value={fromDate} onChange={e => setFromDate(e.target.value)} style={inp} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To</label>
                <input type="date" className="form-input" value={toDate} onChange={e => setToDate(e.target.value)} style={inp} />
              </div>
              <div className="filter-section">
                {singleBu ? (
                  <>
                    <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Business Unit</label>
                    <div style={{ fontSize: '0.8125rem' }}>{buOptions[0].label}</div>
                    <div style={{ ...muted, fontSize: '0.6875rem', marginTop: '0.25rem' }}>The business unit you have access to.</div>
                  </>
                ) : (
                  <>
                    <SyutSelect label="Business Units" isMulti options={buOptions} value={buIds}
                      onChange={v => setBuIds((v as string[]) || [])}
                      placeholder="All my business units" size="sm" menuPortalTarget={document.body} />
                    <div style={{ ...muted, fontSize: '0.6875rem', marginTop: '0.25rem' }}>Empty = every business unit you have access to.</div>
                  </>
                )}
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeZero} onChange={e => setIncludeZero(e.target.checked)} />
                  Show ledgers with no balance or movement
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setFromDate(defaultFrom); setToDate(defaultTo); setBuIds([]); setIncludeZero(false); }}>Reset</button>
              <button className="btn btn-primary" onClick={apply} disabled={loading}>{loading ? 'Loading…' : 'Apply'}</button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader filters={[
        { label: 'Period', value: `${fmtDate(applied.fromDate)} — ${fmtDate(applied.toDate)}` },
        { label: 'Business Units', value: buLabel },
        { label: 'Currency', value: data?.baseCurrencyCode ?? '—' },
      ]} />

      {/* ---------------- Level 2: the ledger ---------------- */}
      {inLedger && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', border: '1px solid var(--border-color)', borderTop: 'none', borderRadius: '0 0 0.5rem 0.5rem' }}>
          {!statement ? <div style={{ ...muted, padding: '2rem', textAlign: 'center' }}>Loading…</div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ background: 'var(--grid-header-bg, #dbeafe)', position: 'sticky', top: 0 }}>
                  <th style={{ ...th, textAlign: 'left', width: 100 }}>Date</th>
                  <th style={{ ...th, textAlign: 'left', width: 150 }}>Voucher</th>
                  <th style={{ ...th, textAlign: 'left', width: 140 }}>Type</th>
                  <th style={{ ...th, textAlign: 'left' }}>Particulars</th>
                  <th style={{ ...th, width: 120 }}>Debit</th>
                  <th style={{ ...th, width: 120 }}>Credit</th>
                  <th style={{ ...th, width: 150 }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ fontWeight: 600, background: 'var(--bg-secondary, #f8fafc)' }}>
                  <td style={{ ...td, textAlign: 'left' }} colSpan={4}>
                    Opening balance{ledgerRow!.isProfitAndLoss && data?.financialYearStart ? ` (from the financial-year start, ${fmtDate(data.financialYearStart)})` : ''}
                  </td>
                  <td style={td}></td><td style={td}></td>
                  <td style={td}>{fmtBal(statement.openingBalance)}</td>
                </tr>
                {statement.lines.length === 0 && (
                  <tr><td colSpan={7} style={{ ...td, ...muted, textAlign: 'center' }}>No entries in the period.</td></tr>
                )}
                {statement.lines.map((l, i) => (
                  <tr key={`${l.transactionId}-${i}`} style={{ borderTop: '1px solid var(--border-color)' }}>
                    <td style={{ ...td, textAlign: 'left' }}>{fmtDate(l.transactionDate)}</td>
                    <td style={{ ...td, textAlign: 'left' }}>
                      <button type="button" onClick={() => setVoucherId(l.transactionId)} title="Open the voucher"
                        style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary-color, #2563eb)', cursor: 'pointer', textDecoration: 'underline', fontSize: 'inherit' }}>
                        {l.transactionNo}
                      </button>
                    </td>
                    <td style={{ ...td, textAlign: 'left', ...muted }}>{l.voucherTypeName}</td>
                    <td style={{ ...td, textAlign: 'left', whiteSpace: 'normal' }}>{l.narration}</td>
                    <td style={td}>{money(l.drAmount)}</td>
                    <td style={td}>{money(l.crAmount)}</td>
                    <td style={td}>{fmtBal(l.runningBalance)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border-color)' }}>
                  <td style={{ ...td, textAlign: 'left' }} colSpan={4}>Closing balance</td>
                  <td style={td}>{money(statement.totalDr)}</td>
                  <td style={td}>{money(statement.totalCr)}</td>
                  <td style={td}>{fmtBal(statement.closingBalance)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ---------------- Level 1: the trial balance ---------------- */}
      {!inLedger && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', border: '1px solid var(--border-color)', borderTop: 'none', borderRadius: '0 0 0.5rem 0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border-color)', flexWrap: 'wrap' }}>
            <button className={`btn ${level === 'GROUPS' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: '0.78rem', padding: '0.25rem 0.7rem' }} onClick={() => changeLevel('GROUPS')}>Groups only</button>
            <button className={`btn ${level === 'LEDGERS' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: '0.78rem', padding: '0.25rem 0.7rem' }} onClick={() => changeLevel('LEDGERS')}>All ledgers</button>
            <span style={{ ...muted, fontSize: '0.75rem' }}>Click ▸ to open a level · click a ledger to see its entries</span>
            {data && (
              <span style={{ marginLeft: 'auto', fontSize: '0.8rem', fontWeight: 600, color: balanced ? '#059669' : '#dc2626' }}>
                {balanced ? 'Debits = credits' : `Out by ${money(Math.abs(data.difference))}`}
              </span>
            )}
          </div>
          {data?.warnings.map(w => (
            <div key={w} style={{ display: 'flex', gap: '0.5rem', padding: '0.4rem 0.75rem', borderBottom: '1px solid var(--border-color)', fontSize: '0.78rem', borderLeft: '3px solid #f59e0b' }}>
              <IconSystem.info size={14} /><span>{w}</span>
            </div>
          ))}
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {!data ? (
              <div style={{ ...muted, padding: '2rem', textAlign: 'center' }}>{loading ? 'Loading…' : 'Choose the period under Filters.'}</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                <thead>
                  <tr style={{ background: 'var(--grid-header-bg, #dbeafe)', position: 'sticky', top: 0, zIndex: 1 }}>
                    <th style={{ ...th, textAlign: 'left' }}>Particulars</th>
                    <th style={th}>Opening Dr</th>
                    <th style={th}>Opening Cr</th>
                    <th style={th}>Debit</th>
                    <th style={th}>Credit</th>
                    <th style={th}>Closing Dr</th>
                    <th style={th}>Closing Cr</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(n => {
                    const hasKids = n.children.length > 0;
                    const isOpen = expanded.has(n.key);
                    const strong = n.kind === 'type' || n.kind === 'subtype';
                    return (
                      <tr key={n.key} style={{ borderTop: '1px solid var(--border-color)', background: n.kind === 'type' ? 'var(--bg-secondary, #f8fafc)' : undefined }}>
                        <td style={{ padding: '0.3rem 0.6rem', paddingLeft: `${0.6 + n.depth * 1.1}rem`, fontWeight: strong ? 700 : 400 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                            {hasKids ? (
                              <button type="button" onClick={() => toggle(n.key)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', display: 'inline-flex' }}
                                title={isOpen ? 'Collapse' : 'Expand'}>
                                {isOpen ? <IconSystem.chevronDown size={14} /> : <IconSystem.chevronRight size={14} />}
                              </button>
                            ) : <span style={{ width: 14 }} />}
                            {n.ledger ? (
                              <button type="button" onClick={() => openLedger(n.ledger!)} title="See the ledger's entries"
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--primary-color, #2563eb)', textAlign: 'left', fontSize: 'inherit' }}>
                                <span style={{ ...muted, marginRight: '0.4rem' }}>{n.ledger.ledgerCode}</span>{n.label}
                              </button>
                            ) : <span style={{ fontStyle: n.kind === 'party' || n.kind === 'plbf' ? 'italic' : undefined }}>{n.label}</span>}
                          </span>
                        </td>
                        {amountCells(n, strong)}
                      </tr>
                    );
                  })}
                  <tr style={{ borderTop: '2px solid var(--text-muted)', fontWeight: 700, position: 'sticky', bottom: 0, background: 'var(--bg-primary, #fff)' }}>
                    <td style={{ padding: '0.45rem 0.6rem' }}>Total</td>
                    <td style={{ ...td, fontWeight: 700 }}>{money(data.openingDebit)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{money(data.openingCredit)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{money(data.periodDebit)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{money(data.periodCredit)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{money(data.closingDebit)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{money(data.closingCredit)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      <VoucherViewModal transactionId={voucherId} onClose={() => setVoucherId(null)} />
    </EnterpriseScreenShell>
  );
};

export default TrialBalanceScreen;
